import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  createBrokerageCanvasserDirectoryApi,
  type BrokerageCanvasserDirectoryApi,
  type CanvasserDirectory,
} from '../auth/brokerageCanvasserDirectoryApi';
import { resolveClientEnv } from '../graphql/config/resolveClientEnv';

export const CANVASSER_DIRECTORY_LOADING_LABEL = 'Loading…';
export const CANVASSER_DIRECTORY_UNKNOWN_LABEL = 'Unknown';
const EMPTY_DIRECTORY: CanvasserDirectory = new Map();

export type BrokerageCanvasserDirectoryStatus = 'loading' | 'ready' | 'error';

export type BrokerageCanvasserDirectoryContextValue = {
  status: BrokerageCanvasserDirectoryStatus;
  retry: () => void;
  resolveLabel: (canvasserId: string) => string;
};

type DirectoryState = {
  brokerageId: string | null;
  api: BrokerageCanvasserDirectoryApi;
  status: BrokerageCanvasserDirectoryStatus;
  directory: CanvasserDirectory;
};

const BrokerageCanvasserDirectoryContext = createContext<BrokerageCanvasserDirectoryContextValue | null>(null);

export type BrokerageCanvasserDirectoryProviderProps = {
  brokerageId: string | null | undefined;
  api?: BrokerageCanvasserDirectoryApi;
  children: ReactNode;
};

const normalizeBrokerageId = (brokerageId: string | null | undefined): string | null => {
  if (typeof brokerageId !== 'string') return null;

  const trimmed = brokerageId.trim();
  return trimmed || null;
};

const createDirectoryState = (
  brokerageId: string | null,
  api: BrokerageCanvasserDirectoryApi,
  status: BrokerageCanvasserDirectoryStatus,
  directory: CanvasserDirectory = EMPTY_DIRECTORY,
): DirectoryState => ({ brokerageId, api, status, directory });

export const BrokerageCanvasserDirectoryProvider = ({
  brokerageId,
  api,
  children,
}: BrokerageCanvasserDirectoryProviderProps) => {
  const defaultApiRef = useRef<BrokerageCanvasserDirectoryApi | null>(null);
  const directoryApi = api ?? (defaultApiRef.current ??= createBrokerageCanvasserDirectoryApi({ env: resolveClientEnv() }));
  const normalizedBrokerageId = normalizeBrokerageId(brokerageId);
  const [requestVersion, setRequestVersion] = useState(0);
  const [state, setState] = useState<DirectoryState>(() =>
    createDirectoryState(
      normalizedBrokerageId,
      directoryApi,
      normalizedBrokerageId === null ? 'error' : 'loading',
    ),
  );
  const activeControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (normalizedBrokerageId === null) {
      activeControllerRef.current?.abort();
      activeControllerRef.current = null;
      setState(createDirectoryState(null, directoryApi, 'error'));
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    activeControllerRef.current = controller;
    setState(createDirectoryState(normalizedBrokerageId, directoryApi, 'loading'));

    let request: Promise<CanvasserDirectory>;
    try {
      request = directoryApi.loadCanvasserDirectory(normalizedBrokerageId, { signal: controller.signal });
    } catch (error) {
      request = Promise.reject(error);
    }

    void request
      .then(directory => {
        if (!active || controller.signal.aborted || activeControllerRef.current !== controller) return;

        setState(createDirectoryState(normalizedBrokerageId, directoryApi, 'ready', directory));
      })
      .catch(() => {
        if (!active || controller.signal.aborted || activeControllerRef.current !== controller) return;

        setState(createDirectoryState(normalizedBrokerageId, directoryApi, 'error'));
      });

    return () => {
      active = false;
      controller.abort();
      if (activeControllerRef.current === controller) activeControllerRef.current = null;
    };
  }, [directoryApi, normalizedBrokerageId, requestVersion]);

  const retry = useCallback((): void => {
    activeControllerRef.current?.abort();
    activeControllerRef.current = null;
    setState(
      createDirectoryState(
        normalizedBrokerageId,
        directoryApi,
        normalizedBrokerageId === null ? 'error' : 'loading',
      ),
    );
    setRequestVersion(currentVersion => currentVersion + 1);
  }, [directoryApi, normalizedBrokerageId]);

  const stateMatchesCurrentRequest =
    normalizedBrokerageId !== null && state.brokerageId === normalizedBrokerageId && state.api === directoryApi;
  const status: BrokerageCanvasserDirectoryStatus = stateMatchesCurrentRequest
    ? state.status
    : normalizedBrokerageId === null
      ? 'error'
      : 'loading';
  const directory = stateMatchesCurrentRequest ? state.directory : EMPTY_DIRECTORY;

  const resolveLabel = useCallback(
    (canvasserId: string): string => {
      if (status === 'loading') return CANVASSER_DIRECTORY_LOADING_LABEL;
      if (status === 'error') return canvasserId;

      return directory.get(canvasserId.trim()) ?? CANVASSER_DIRECTORY_UNKNOWN_LABEL;
    },
    [directory, status],
  );

  const value = useMemo<BrokerageCanvasserDirectoryContextValue>(
    () => ({ status, retry, resolveLabel }),
    [resolveLabel, retry, status],
  );

  return <BrokerageCanvasserDirectoryContext.Provider value={value}>{children}</BrokerageCanvasserDirectoryContext.Provider>;
};

export const useBrokerageCanvasserDirectory = (): BrokerageCanvasserDirectoryContextValue => {
  const value = useContext(BrokerageCanvasserDirectoryContext);
  if (value === null) {
    throw new Error('useBrokerageCanvasserDirectory must be used within BrokerageCanvasserDirectoryProvider.');
  }

  return value;
};
